import { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  Container,
  CircularProgress,
} from "@mui/material";
import {
  ShoppingCart,
  Person,
  LocalShipping,
  Receipt,
  CheckCircle,
  Paid,
  ArrowUpward,
  ArrowDownward,
} from "@mui/icons-material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import GroupsIcon from "@mui/icons-material/Groups";
import StorefrontIcon from "@mui/icons-material/Storefront";
import { useNavigate } from "react-router-dom";
import axios from "../axiosInstance";
import PageTitle from "../Components/PageTitle";
const BuyerDashboard = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState("last7days");
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    fetchDashboardData();
  }, [timePeriod]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const daysMap = {
        last7days: 7,
        last30days: 30,
        last90days: 90,
      };

      const days = daysMap[timePeriod] || 7;
      const response = await axios.get(`/api/v1/dashboard/buyer?days=${days}`);

      if (response.data.success) {
        setDashboardData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const metricCards = [
    {
      icon: <Person sx={{ color: "#3b82f6" }} />,
      title: "Total Seller",
      value: dashboardData?.totalSellers?.toString() || "0",
      color: "#eff6ff",
    },
    {
      icon: <LocalShipping sx={{ color: "#f59e0b" }} />,
      title: "Pending Orders",
      value: dashboardData?.ordersInTransit?.toString() || "0",
      color: "#fef3c7",
      badge: {
        label: `${dashboardData?.ordersInTransit || 0} Active`,
        color: "#fef3c7",
        textColor: "#f59e0b",
      },
    },
    {
      icon: <Receipt sx={{ color: "#ef4444" }} />,
      title: "Pending Invoices",
      value: dashboardData?.pendingInvoices?.toString() || "0",
      color: "#fee2e2",
      badge: {
        label: `₹${
          dashboardData?.totalPayables?.toLocaleString("en-IN") || "0"
        }`,
        color: "#fee2e2",
        textColor: "#ef4444",
      },
    },
    {
      icon: <SupportAgentIcon sx={{ color: "#ffffff" }} />,
      title: "Need Help?",
      value: "Support",
      color: "#546073",
      route: "/buyer-dashboard/support",
      smallValue: true,
    },
  ];

  const quickActions = [
    {
      icon: <StorefrontIcon sx={{ color: "#ffffff" }} />,
      title: "Seller Request",
      color: "#546073",
      route: "/buyer-dashboard/seller-requests",
    },
    {
      icon: <CheckCircle sx={{ color: "#ffffff" }} />,
      title: "View My Orders",
      color: "#e7bf7a",
      route: "/buyer-dashboard/my-orders",
    },
    {
      icon: <Receipt sx={{ color: "#ffffff" }} />,
      title: "Pending Invoices",
      color: "#58695e",
      route: "/buyer-dashboard/pending-invoices",
    },
  ];

  const getChartData = () => {
    if (!dashboardData?.graphData || dashboardData.graphData.length === 0) {
      return [
        { month: "Dec", value: 104.18 },
        { month: "Jan", value: 0.33 },
      ];
    }

    return dashboardData.graphData.map((item) => ({
      month: item.name,
      value: item.expense,
    }));
  };

  const chartData = getChartData();
  const barColors = chartData.map((item, index) =>
    index === chartData.length - 1 ? "#0b4064" : "#93c5fd",
  );
  const barOpacities = chartData.map((item, index) =>
    index === chartData.length - 1 ? 1 : 0.7,
  );

  // Format Live Feed according to screenshot design
  const getFeedItems = () => {
    if (!dashboardData?.liveFeed || dashboardData.liveFeed.length === 0) {
      return [];
    }

    return dashboardData.liveFeed.map((item, index) => {
      const isPayment = item.type === "payment";
      const amount = item.amount;
      const isNegativePayment = amount.startsWith("-");
      const formattedAmount = isNegativePayment
        ? amount.replace("-₹", "₹")
        : amount;

      // Format time relative to now
      const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffDays > 0) {
          return `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
        } else if (diffHours > 0) {
          return `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
        } else if (diffMinutes > 0) {
          return `${diffMinutes} ${
            diffMinutes === 1 ? "minute" : "minutes"
          } ago`;
        } else {
          return "Just now";
        }
      };

      return {
        id: item.id,
        sellerName: item.name,
        action:
          item.action === "Payment made to seller"
            ? "Payment received"
            : item.action,
        amount: formattedAmount,
        time: getTimeAgo(item.time),
        type: item.type,
        icon: isPayment ? (
          <Paid sx={{ color: "#10b981", fontSize: 16 }} />
        ) : (
          <ShoppingCart sx={{ color: "#0b4064", fontSize: 16 }} />
        ),
        isNegative: isNegativePayment,
      };
    });
  };

  const feedItems = getFeedItems();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "#f8f9fa",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <PageTitle
        title="Buyer Dashboard - BizBridge"
        description="Overview of your buyer activities and insights"
        keywords="buyer dashboard, buyer insights, orders, invoices, spending trends"
        url="/buyer-dashboard"
      />

      <Box sx={{ bgcolor: "#f8f9fa", minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
              flexWrap: "none",
              gap: 3,
            }}
          >
            {/* <Typography
            variant={isMobile ? "h5" : "h4"}
            sx={{ fontWeight: "bold", color: "#1e293b" }}
          >
            Buyer Insights
          </Typography> */}

            <Typography variant="h5" sx={{ fontWeight: 800, color: "#1e293b" }}>
              Buyer Insights
            </Typography>

            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={timePeriod}
                onChange={(e) => setTimePeriod(e.target.value)}
                sx={{
                  bgcolor: "white",
                  border: "1px solid #e2e8f0",
                  "& .MuiOutlinedInput-notchedOutline": {
                    border: "none",
                  },
                }}
              >
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="last90days">Last 90 Days</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Card
            sx={{
              bgcolor: "#1e293b",
              color: "white",
              mb: 3,
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
            }}
            elevation={0}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", md: "center" },
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: "#94a3b8", mb: 1 }}>
                  {getGreeting()}, Sachin Saini
                </Typography>
                <Typography
                  variant={isMobile ? "h6" : "h4"}
                  sx={{ fontWeight: "bold" }}
                >
                  Your spending is{" "}
                  <Box
                    component="span"
                    sx={{
                      color:
                        dashboardData?.expenseGrowth >= 0
                          ? "#10b981"
                          : "#ef4444",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    {dashboardData?.expenseGrowth >= 0 ? (
                      <>
                        <ArrowUpward
                          sx={{ fontSize: isMobile ? "0.9rem" : "1.1rem" }}
                        />
                        up
                      </>
                    ) : (
                      <>
                        <ArrowDownward
                          sx={{ fontSize: isMobile ? "0.9rem" : "1.1rem" }}
                        />
                        down
                      </>
                    )}{" "}
                    {Math.abs(dashboardData?.expenseGrowth || 0)}%
                  </Box>{" "}
                  this month.
                </Typography>
              </Box>

              <Button
                variant="contained"
                onClick={() => navigate("/buyer-dashboard/seller-network")}
                startIcon={<GroupsIcon fontSize="large" />}
                fullWidth={isMobile}
                sx={{
                  bgcolor: "white",
                  color: "#0b4064",
                  "&:hover": {
                    bgcolor: "#f1f5f9",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  },
                  px: 5.5,
                  py: 1.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  transition: "all 0.3s ease",
                }}
              >
                Seller Network
              </Button>
            </Box>
          </Card>

          <Box sx={{ mb: { xs: 3, lg: 2 } }}>
            <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
              <Grid item xs={12} lg={8}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1.1rem",
                      }}
                    >
                      Order Summary
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Grid container spacing={2}>
                      {metricCards.map((card, index) => (
                        <Grid item xs={12} sm={6} md={6} key={index}>
                          <Card
                            elevation={0}
                            onClick={() => {
                              if (card.route) {
                                navigate(card.route);
                              }
                            }}
                            sx={{
                              border: "1px solid #e2e8f0",
                              height: "100%",
                              borderRadius: 2,
                              transition: "all 0.3s ease",
                              cursor: card.route ? "pointer" : "default",
                              "&:hover": {
                                boxShadow: card.route
                                  ? "0 4px 12px rgba(0,0,0,0.08)"
                                  : "none",
                                transform: card.route
                                  ? "translateY(-4px)"
                                  : "none",
                              },
                            }}
                          >
                            <CardContent sx={{ p: 2.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "flex-start",
                                  mb: 2,
                                }}
                              >
                                <Avatar
                                  sx={{
                                    bgcolor: card.color,
                                    width: 48,
                                    height: 48,
                                  }}
                                >
                                  {card.icon}
                                </Avatar>

                                {card.badge && (
                                  <Chip
                                    label={card.badge.label}
                                    size="small"
                                    sx={{
                                      bgcolor: card.badge.color,
                                      color: card.badge.textColor,
                                      fontWeight: 600,
                                      fontSize: "0.7rem",
                                      height: 24,
                                    }}
                                  />
                                )}
                              </Box>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1, fontSize: "0.875rem" }}
                              >
                                {card.title}
                              </Typography>

                              <Typography
                                sx={{
                                  fontWeight: "bold",
                                  fontSize: card.smallValue
                                    ? "1.75rem"
                                    : "2rem",
                                  color: card.smallValue
                                    ? "#0b4064"
                                    : "#0b4064",
                                }}
                              >
                                {card.value}
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    overflow: "hidden",
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: "#1e293b",
                        fontSize: "1.1rem",
                      }}
                    >
                      Quick Actions
                    </Typography>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      {quickActions.map((action, index) => (
                        <Card
                          key={index}
                          elevation={0}
                          onClick={() => navigate(action.route)}
                          sx={{
                            border: "1px solid #e2e8f0",
                            borderRadius: 2,
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            "&:hover": {
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              transform: "translateX(4px)",
                              borderColor: action.color,
                              bgcolor: "#f8fafc",
                            },
                          }}
                        >
                          <CardContent sx={{ p: 5.5 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: action.color,
                                  width: 44,
                                  height: 44,
                                }}
                              >
                                {action.icon}
                              </Avatar>

                              <Typography
                                variant="body1"
                                sx={{
                                  fontSize: "0.95rem",
                                  fontWeight: 600,
                                  color: "#1e293b",
                                }}
                              >
                                {action.title}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Grid container spacing={3} sx={{ alignItems: "flex-start" }}>
              <Grid item xs={12} lg={8}>
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 1.5,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "1.1rem",
                        }}
                      >
                        Spending Trend
                      </Typography>
                      <Chip
                        label="Monthly View"
                        size="small"
                        variant="outlined"
                        sx={{
                          borderColor: "#64748b",
                          fontSize: "0.75rem",
                        }}
                      />
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    <ResponsiveContainer
                      width="100%"
                      height={isMobile ? 250 : 400}
                    >
                      <BarChart
                        data={chartData}
                        margin={{
                          top: 20,
                          right: isMobile ? 10 : 30,
                          left: isMobile ? -20 : 0,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          stroke="#cbd5e1"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="month"
                          tick={{
                            fill: "#64748b",
                            fontSize: isMobile ? 10 : 12,
                          }}
                          axisLine={{ stroke: "#cbd5e1" }}
                          tickLine={false}
                        />
                        <YAxis
                          tick={{
                            fill: "#64748b",
                            fontSize: isMobile ? 9 : 11,
                          }}
                          axisLine={{ stroke: "#cbd5e1" }}
                          tickLine={false}
                          domain={[0, "dataMax + 10"]}
                          tickFormatter={(value) => `₹${value}k`}
                        />
                        <Tooltip
                          formatter={(value) => [`₹${value}k`, "Expense"]}
                          contentStyle={{
                            backgroundColor: "white",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            fontSize: isMobile ? "12px" : "14px",
                          }}
                          cursor={false}
                          labelFormatter={(label) => `Month: ${label}`}
                        />
                        <Bar
                          dataKey="value"
                          name="Expense"
                          radius={[8, 8, 0, 0]}
                          maxBarSize={isMobile ? 40 : 80}
                        >
                          {chartData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={barColors[index % barColors.length]}
                              opacity={
                                barOpacities[index % barOpacities.length]
                              }
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                {/* Live Feed Card - Updated Design with real data */}
                <Card
                  elevation={0}
                  sx={{
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    height: "fit-content",
                  }}
                >
                  <Box
                    sx={{
                      px: 3,
                      py: 2,
                      borderBottom: "1px solid #e2e8f0",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: "#1e293b",
                          fontSize: "1.1rem",
                        }}
                      >
                        Live Feed
                      </Typography>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            bgcolor: "#22c55e",
                            animation: "pulse 1.5s infinite",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#22c55e",
                            fontWeight: 500,
                            fontSize: "0.875rem",
                          }}
                        >
                          Live
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {feedItems.length > 0 ? (
                        feedItems.map((item, index) => (
                          <Box
                            key={item.id}
                            sx={{
                              px: 2,
                              py: 0.8,
                              borderBottom:
                                index < feedItems.length - 1
                                  ? "1px solid #f1f5f9"
                                  : "none",
                              "&:hover": {
                                bgcolor: "#f8fafc",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                px: 3,
                                py: 1.5,
                                borderBottom: "1px solid #eef2f7",
                                "&:hover": {
                                  bgcolor: "#f8fafc",
                                },
                              }}
                            >
                              {/* Icon */}
                              <Box
                                sx={{
                                  width: 36,
                                  height: 36,
                                  borderRadius: "50%",
                                  bgcolor:
                                    item.type === "payment"
                                      ? "#eafff1"
                                      : "#eef4ff",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  mr: 2,
                                  flexShrink: 0,
                                }}
                              >
                                {item.icon}
                              </Box>

                              {/* Seller + Action */}
                              <Box sx={{ flex: 1, lineHeight: 1.1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    color: "#0f172a",
                                  }}
                                >
                                  {item.sellerName}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: "0.8rem",
                                    color: "#6b7280",
                                    mt: 0.2,
                                  }}
                                >
                                  {item.action}
                                </Typography>
                              </Box>

                              {/* Amount + Time */}
                              <Box sx={{ textAlign: "right", lineHeight: 1.2 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.95rem",
                                    color:
                                      item.type === "payment"
                                        ? "#22c55e"
                                        : "#0f172a",
                                  }}
                                >
                                  {item.amount}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: "0.75rem",
                                    color: "#6b7280",
                                    mt: 0.2,
                                  }}
                                >
                                  {item.time}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ))
                      ) : (
                        <Box
                          sx={{
                            px: 3,
                            py: 4,
                            textAlign: "center",
                          }}
                        >
                          <Typography color="text.secondary">
                            No recent activity
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Container>

        <style>
          {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
        </style>
      </Box>
    </>
  );
};

export default BuyerDashboard;
